import {
  EligibilityResourceType,
  EVIDENCE_INCLUDE_VALUES,
  evidenceContract,
} from "@clipboard-health/contract-documents-service";
import { get } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { DOCUMENT_EVIDENCES_QUERY_KEY } from "@src/appV2/redesign/Evidence/constants";
import { type EvidenceDefinition } from "@src/appV2/redesign/Evidence/types";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { z } from "zod";

const queryParamsSchema = z.object({
  page: z.object({
    size: z.number(),
  }),
  filter: z.object({
    mongoDocumentId: z.string(),
  }),
  include: z.array(z.string()),
});

const responseSchema = evidenceContract.list.responses[200];

export type EvidenceData = z.infer<typeof responseSchema>["data"][number];

export function useGetDocumentEvidences(mongoDocumentId: string) {
  const url = `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/v2/evidences`;

  const query = useQuery({
    queryKey: [DOCUMENT_EVIDENCES_QUERY_KEY, mongoDocumentId],
    queryFn: async () => {
      const response = await get({
        url,
        queryParams: {
          page: { size: 100 },
          filter: { mongoDocumentId },
          // Embed each evidence's linked definition in `included` so the review
          // modal can render the selected value + property form without a
          // separate full-catalog fetch (see ACT-4986 / ACT-4987).
          include: [EVIDENCE_INCLUDE_VALUES.EVIDENCE_DEFINITION],
        },
        queryParamsSchema,
        responseSchema,
      });
      return response.data;
    },
    enabled: mongoDocumentId.length > 0,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_DOCUMENT_EVIDENCES_FAILURE },
  });

  // Memoized so the derived array keeps a stable identity across renders (it
  // feeds a downstream `useMemo` in the review modal).
  const includedDefinitions = useMemo<EvidenceDefinition[]>(
    () =>
      (query.data?.included ?? []).filter(
        (resource): resource is EvidenceDefinition =>
          resource.type === EligibilityResourceType.EVIDENCE_DEFINITION
      ),
    [query.data?.included]
  );

  return {
    evidences: query.data?.data ?? [],
    includedDefinitions,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
